/**
 * Utility functions for API configuration
 */

/**
 * Sets a custom API URL to be used for all subsequent requests
 * @param url - The base URL of the API (e.g., 'http://localhost:4000/api')
 * @param persist - If true, saves the URL to localStorage for future sessions
 */
export const setApiUrl = (url: string, persist: boolean = false): void => {
  try {
    // Remove any trailing slashes
    const cleanUrl = url.replace(/\/+$/, '');
    
    // Store in localStorage if persistence is requested
    if (persist) {
      localStorage.setItem('apiUrl', cleanUrl);
    } else {
      // If not persisting, remove any previously saved URL
      localStorage.removeItem('apiUrl');
    }
    
    // Reload the page to apply changes
    window.location.href = `${window.location.pathname}?apiUrl=${encodeURIComponent(cleanUrl)}`;
  } catch (error) {
    console.error('Failed to set API URL:', error);
  }
};

/**
 * Resets the API URL to the default value from environment variables
 */
export const resetApiUrl = (): void => {
  try {
    // Remove any URL parameters and localStorage overrides
    const url = new URL(window.location.href);
    url.searchParams.delete('apiUrl');
    window.history.replaceState({}, '', url.toString());
    
    localStorage.removeItem('apiUrl');
    
    // Reload to apply default URL
    window.location.reload();
  } catch (error) {
    console.error('Failed to reset API URL:', error);
  }
};

/**
 * Gets the current API URL being used
 */
export const getCurrentApiUrl = (): string => {
  // In production, always use env variable
  if (!import.meta.env.DEV) {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api';
  }
  // In development, allow override for flexibility
  return localStorage.getItem('apiUrl') ||
    new URLSearchParams(window.location.search).get('apiUrl') ||
    import.meta.env.VITE_API_BASE_URL ||
    'http://localhost:4000/api';
};
